import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { VOCABULARIES, type VocabularyItem, type VocabularyKey } from '../evidenceVocabulary';
import { sampleVocabulary } from '../utils/sampleEvidence';

/**
 * Creates or edits a kind or a type — the two vocabularies the intake grows.
 *
 * A KIND carries the one choice that changes behaviour: whether evidence of that kind IS a file.
 * It is two radios rather than a checkbox, because "kept on a shelf" is a claim, not the absence
 * of one. Changing it changes the NEXT intake only; items already received keep the custody form
 * they were received under.
 */
export function EvidenceVocabularyModal({
  vocabulary,
  editing,
  onClose,
  onSaved,
}: {
  vocabulary: VocabularyKey;
  editing: VocabularyItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const config = VOCABULARIES[vocabulary];
  const prefix = `evidence-${config.slug}`;

  const [name, setName] = useState(editing?.name ?? '');
  const [storesFile, setStoresFile] = useState(String(editing?.storesFile ?? false));
  const [description, setDescription] = useState(editing?.description ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = name.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      storesFile: storesFile === 'true',
    };

    try {
      if (editing) await config.api.update(editing.id, request);
      else await config.api.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError(`Could not save the ${config.singular}.`, 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: prefix,
        // A RadioGroup puts its id on each OPTION, so the field maps to the chosen one.
        fieldIds: { storesFile: `${prefix}-stores-file-${storesFile}` },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : `New ${config.singular}`}
      size="lg"
      id={`${prefix}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleVocabulary(config.singular);
          setName(sample.name);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{ id: `${prefix}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${prefix}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${prefix}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id={`${prefix}-name`}
          label="Name"
          required
          description={config.nameDescription}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        {config.hasFileFlag ? (
          <RadioGroup
            id={`${prefix}-stores-file`}
            label="Kept as"
            required
            value={storesFile}
            onChange={setStoresFile}
            disabled={saving}
            options={[
              {
                value: 'false',
                label: 'Kept on a shelf',
                description: 'An object, stored in a storage location. Its documents are attached; it has no evidence file.',
              },
              {
                value: 'true',
                label: 'Stored as a file',
                description: 'The evidence IS a file — uploaded at intake, fingerprinted, and checked on every download.',
              },
            ]}
            error={error?.fieldError('storesFile')}
          />
        ) : null}

        <Textarea
          id={`${prefix}-description`}
          label="Description"
          description="Optional. What belongs under it, for whoever picks it next."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        {config.hasFileFlag && editing ? (
          <p className="text-xs text-slate-500">
            Changing how it is kept applies to evidence received from now on. Items already received
            keep the custody form they were received under.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
